import { inject as controller } from '@ember/controller';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { includes } from '@nullvoxpopuli/ember-composable-helpers';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import add from 'ember-math-helpers/helpers/add';
import PowerSelect from 'ember-power-select/components/power-select';
import RouteTemplate from 'ember-route-template';
import set from 'ember-set-helper/helpers/set';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, eq, not } from 'ember-truth-helpers';
import MacAddressMappingsTable from 'garaje/components/mac-address-mappings/table';
import SlideToggle from 'garaje/components/slide-toggle';
import UiCard from 'garaje/components/ui/card';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import MessageBox from 'garaje/components/ui/classic/message-box';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiHeader from 'garaje/components/ui/header';
import UiPage from 'garaje/components/ui/page';
import UiButton from 'garaje/components/ui-button';
import UiPill from 'garaje/components/ui-pill';
import type { FillColorString } from 'garaje/components/ui-pill';
import type CompanyConfiguration from 'garaje/models/company-configuration';
import type SkinnyLocation from 'garaje/models/skinny-location';
import type { LocationOverviewManageAccountSettingsRouteModel } from 'garaje/pods/location-overview/manage/account-settings/route';
import {
  GEOFENCE_RADIUS_DEFAULT,
  GEOFENCE_RADIUS_MAX,
  GEOFENCE_RADIUS_MIN,
} from 'garaje/pods/location-overview/manage/account-settings/utils/geofence-radius-bounds';
import type ProtectedController from 'garaje/pods/protected/controller';
import type AjaxService from 'garaje/services/ajax';
import type AuthzService from 'garaje/services/authz';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type InactivityService from 'garaje/services/inactivity';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import type StateService from 'garaje/services/state';
import { AFTER_HOURS_ABSENCE_MINUTES_DEFAULT, type BusinessHoursPayload } from 'garaje/utils/presence/business-hours';
import type {
  MultiSignalPolicyPayload,
  PresenceSettingsResponse,
  SettingsPayload,
  SignalTypeAbsencePayload,
  SignoutPayload,
} from 'garaje/utils/presence/presence-settings-types';
import { Permission } from 'garaje/utils/ui-permissions';
import urlBuilder from 'garaje/utils/url-builder';
import { cached, localCopy } from 'tracked-toolbox';

interface LocationOption {
  location: SkinnyLocation;
  disabled: boolean;
}

enum INACTIVITY_TIME_OPTIONS {
  FIFTEEN_MINS = '15 minutes',
  THIRTY_MINS = '30 minutes',
  ONE_HR = '1 hour',
  TWO_HRS = '2 hours',
}

type MillisecondsToTimeType = {
  [key: number]: INACTIVITY_TIME_OPTIONS;
};

const MILLISECONDS_TO_TIME_MAP: MillisecondsToTimeType = {
  900000: INACTIVITY_TIME_OPTIONS.FIFTEEN_MINS,
  1800000: INACTIVITY_TIME_OPTIONS.THIRTY_MINS,
  3600000: INACTIVITY_TIME_OPTIONS.ONE_HR,
  7200000: INACTIVITY_TIME_OPTIONS.TWO_HRS,
};

// Canonical display order for signal-type pills.
const SIGNAL_TYPE_ORDER = ['badge', 'wifi', 'sso', 'mdm', 'geo-location'];

// Signal type pill display config for the collapsed policy view. Colors map to the shared
// `UiPill` component's `@fill` palette, whose class names are statically defined and therefore
// survive the build-time CSS purge (unlike dynamically-assembled Tailwind class names).
const SIGNAL_TYPE_PILL_CONFIG: Record<string, { label: string; fill: FillColorString }> = {
  badge: { label: 'Access Control', fill: 'blueLightest' },
  wifi: { label: 'Wi-Fi', fill: 'blueLightest' },
  sso: { label: 'SSO', fill: 'blueLightest' },
  mdm: { label: 'MDM', fill: 'blueLightest' },
  'geo-location': { label: 'Geo Location', fill: 'blueLightest' },
};

interface LocationOverviewManageAccountSettingsSignature {
  Args: {
    model: LocationOverviewManageAccountSettingsRouteModel;
  };
}

export class LocationOverviewManageAccountSettingsTemplate extends Component<LocationOverviewManageAccountSettingsSignature> {
  @service declare ajax: AjaxService;
  @service declare abilities: AbilitiesService;
  @service declare authz: AuthzService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare inactivity: InactivityService;
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare state: StateService;
  @service declare store: Store;

  @controller('protected') declare protectedController: ProtectedController;

  @tracked isInactivityBoxOpen: boolean = false;
  // Resets once the record arrives, since the remote value starts undefined.
  @localCopy('companyConfiguration.sessionInactivityTimeout', null) declare inactivityTimeout: number | null;

  @tracked isPresenceControlCenterOpen: boolean = false;
  @tracked selectedLocationId: string | null = null;
  @tracked macMappingsSearch = '';
  @tracked macMappingsSource = '';

  @tracked badgeEnabled: boolean = false;
  @tracked wifiEnabled: boolean = false;
  @tracked ssoEnabled: boolean = false;
  @tracked geoLocationEnabled: boolean = false;
  @tracked manualEnabled: boolean = false;
  @tracked mdmEnabled: boolean = false;
  // After-hours sign-in validation (EIP-2017): a single toggle that allows/blocks auto sign-in for
  // signals occurring outside the location's business hours. Defaults true (allowed = today's
  // behavior); when off, the milestone-1 UI writes `enable-after-hours-sign-in: false` to every
  // signal type. Only meaningful when business hours is configured (see `businessHoursConfigured`).
  @tracked enableAfterHoursSignIn: boolean = true;
  @tracked isLoading: boolean = false;
  @tracked error: string | null = null;
  @tracked selectedPresenceTab: 'sign-in' | 'sign-out' = 'sign-in';

  // Multi-signal sign-in policy. `signInMode` is the mutually-exclusive selector on the
  // Sign-in tab between today's per-signal behavior ('any') and the corroboration policy
  // ('multi-signal'); it maps to the stored policy's `enabled` flag. `signalTypePolicies` holds
  // the list of independent policies; a sign-in fires once ANY policy is fully corroborated.
  // Manual is not a valid policy type (only entry-recorder auto-signals corroborate).
  @tracked signInMode: 'any' | 'multi-signal' = 'any';
  // Each inner array is one policy of signal types. Starts with one empty policy so the
  // UI renders a single empty row when entering multi-signal mode for the first time.
  @tracked signalTypePolicies: string[][] = [[]];
  // Indices of policies in expanded (editing) view. Loaded non-empty policies start collapsed;
  // the initial empty row and any newly added rows start expanded.
  @tracked expandedPolicyIndices: Set<number> = new Set([0]);
  // The corroboration window in minutes; null = "unset" (required before an enabled policy can save).
  @tracked multiSignalWindowMinutes: number | null = null;
  // Snapshot of the last saved/loaded policy (API shape), driving the dirty-diff like the trio below.
  @tracked persistedMultiSignalPolicy: MultiSignalPolicyPayload | null = null;

  // Aggregated auto sign-out configuration (any signal resets a single timer)
  @tracked absenceOfSignalsEnabled: boolean = false;
  @tracked absenceOfSignalsDuration: number = 3600000; // Default: 1 hour

  // Per-signal-type auto sign-out configuration (in UI order: badge, wifi, sso, mdm, geo-location)
  @tracked badgeAutoSignOutEnabled: boolean = false;
  @tracked badgeAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked wifiAutoSignOutEnabled: boolean = false;
  @tracked wifiAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked ssoAutoSignOutEnabled: boolean = false;
  @tracked ssoAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked mdmAutoSignOutEnabled: boolean = false;
  @tracked mdmAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked geoLocationAutoSignOutEnabled: boolean = false;

  // After-hours auto sign-out (EIP-2017): opt-in + interval (minutes) applied to signals outside the
  // business-hours window. Only meaningful once business hours is configured (the toggle is disabled
  // otherwise — see `businessHoursConfigured`). Default off / 60 min, matching the server default.
  @tracked afterHoursAutoSignOutEnabled: boolean = false;
  @tracked afterHoursAbsenceMinutes: number = AFTER_HOURS_ABSENCE_MINUTES_DEFAULT;

  // Business hours is configured on the per-location Location settings page now, not here.
  // We still load it (read-only) into this snapshot because the Sign-in / Sign-out after-hours toggles
  // depend on it (see `businessHoursConfigured`) and every tab's Save must carry the persisted
  // business-hours block through the unified PUT so it isn't cleared.
  //
  // The Sign-in / Sign-out tabs use an explicit draft → Save flow: the tracked fields above are the
  // editable drafts, and nothing is persisted until that tab's Save runs. The `persisted*` snapshots
  // below hold each tab's last saved/loaded value (API shape). On any tab's Save we send the full
  // document built from that tab's draft plus the *other* tab's persisted snapshot — so unsaved edits
  // on a different tab are never promoted and never leak into the PUT.
  @tracked persistedBusinessHours: BusinessHoursPayload | null = null;
  @tracked persistedSignIn: SettingsPayload | null = null;
  @tracked persistedSignOut: SignoutPayload | null = null;

  // Location-level "Automatic sign-out" (time-of-day) fallback — lives on the full Location
  // model as `autoSignOutAtMinutesSinceMidnight` (enabled ⇔ non-null, matching
  // components/location-settings/sign-out/auto-sign-out-time.gts). Used only to surface a
  // fallback-explanation tooltip on the PCC Sign-Out tab.
  @tracked locationAutoSignOutEnabled: boolean = false;

  // Per-location geofence radius (meters) — Unit 4 of EIP-3334.
  // Display default of 150 m matches the existing mobile fallback so behavior is unchanged
  // for locations whose server-side column is still NULL. Bounds mirror the server CHECK
  // constraint exactly (see utils/geofence-radius-bounds.ts).
  @tracked geoLocationRadius: number = GEOFENCE_RADIUS_DEFAULT;
  @tracked geoLocationRadiusError: string | null = null;

  get headerTitle(): string {
    const companyName = this.state.currentCompany?.name;
    return companyName ? `Account settings for ${companyName}` : 'Account settings';
  }

  // Undefined on first render: the route loads it without blocking its transition.
  get companyConfiguration(): CompanyConfiguration | undefined {
    return this.args.model?.loadCompanyConfigurationTask.lastSuccessful?.value ?? undefined;
  }

  // A load that settles without a record is not loading: the panels fall back to their default state.
  get isConfigLoading(): boolean {
    const task = this.args.model?.loadCompanyConfigurationTask;
    if (!task) return false;
    return !task.lastSuccessful && !task.lastErrored;
  }

  get inactivityDescription(): INACTIVITY_TIME_OPTIONS | undefined {
    const sessionTimeout: number = this.companyConfiguration?.sessionInactivityTimeout || 7200000;
    return MILLISECONDS_TO_TIME_MAP[sessionTimeout];
  }

  get inactivityOptions(): Array<number> {
    return [900000, 1800000, 3600000, 7200000];
  }

  get isSaveBtnDisabled(): boolean {
    return this.companyConfiguration?.sessionInactivityTimeout === this.inactivityTimeout;
  }

  @cached
  get availableLocations(): LocationOption[] {
    const locations = this.skinnyLocations.currentCompanyLocations || [];

    // Map locations to create copy of location with 'disabled' property updated
    // PowerSelect checks 'disabled' by default and grays out options
    // disabled_to_employees_at (not 'disabled') controls employee visibility
    return locations.map((location) => ({
      location,
      disabled: !!location.disabledToEmployeesAt,
    }));
  }

  get selectedLocation(): LocationOption | null {
    if (!this.selectedLocationId) return null;
    const options = this.availableLocations;

    return options.find((option) => option.location.id === this.selectedLocationId) || null;
  }

  get shouldShowToggles(): boolean {
    return !!this.selectedLocationId;
  }

  get isInactivityTimeoutEnabled(): boolean {
    return !!this.state.features?.canAccessInactivityLogout;
  }

  get isPresenceControlCenterEnabled(): boolean {
    return this.authz.hasPermissionAtCurrentCompany(Permission.ACCOUNT_MANAGEMENT_PRODUCT_SETTINGS_READ);
  }

  get isGeofenceRadiusEnabled(): boolean {
    return this.featureFlags.isEnabled('presence-control-center-geofence-radius');
  }

  // Bounds re-exposed to the template — keeps the values in a single source
  // (utils/geofence-radius-bounds.ts) and out of the markup.
  get geofenceRadiusMin(): number {
    return GEOFENCE_RADIUS_MIN;
  }

  get geofenceRadiusMax(): number {
    return GEOFENCE_RADIUS_MAX;
  }

  get showMacAddressMappings(): boolean {
    return this.abilities.can('view mac-address-mapping');
  }

  get companyId(): string {
    return this.state.currentCompany?.id ?? '';
  }

  get anySignOutSettingEnabled(): boolean {
    return (
      this.absenceOfSignalsEnabled ||
      this.badgeAutoSignOutEnabled ||
      this.wifiAutoSignOutEnabled ||
      this.ssoAutoSignOutEnabled ||
      this.mdmAutoSignOutEnabled ||
      this.geoLocationAutoSignOutEnabled
    );
  }

  // Notice explains that, when PCC will sign employees out on signal absence AND the
  // location also has time-based Automatic sign-out on, the location setting is a fallback.
  get showSignOutFallbackNotice(): boolean {
    return this.locationAutoSignOutEnabled && this.anySignOutSettingEnabled;
  }

  get absenceOfSignalsDurationOptions(): Array<{ value: number; label: string }> {
    return [
      { value: 3600000, label: '1 hour' },
      { value: 7200000, label: '2 hours' },
      { value: 14400000, label: '4 hours' },
      { value: 28800000, label: '8 hours' },
    ];
  }

  get selectedAbsenceOfSignalsDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.absenceOfSignalsDuration);
  }

  get selectedBadgeDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.badgeAutoSignOutDuration);
  }

  get selectedWifiDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.wifiAutoSignOutDuration);
  }

  get selectedSsoDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.ssoAutoSignOutDuration);
  }

  get selectedMdmDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.mdmAutoSignOutDuration);
  }

  // Business hours is "configured" once it's been saved with the master switch on — the only state
  // in which after-hours auto sign-out can take effect (entry-recorder no-ops it otherwise). Gated on
  // the *persisted* config (not the unsaved draft) because the after-hours toggle auto-saves and
  // should reflect what's actually in effect. Shared gate for the after-hours sign-out toggle here and
  // the future after-hours sign-in toggle.
  get businessHoursConfigured(): boolean {
    return this.persistedBusinessHours !== null && this.persistedBusinessHours.enabled;
  }

  // After-hours interval options. Stored as minutes (unlike the signal-absence durations above, which
  // are milliseconds), matching the `after-hours-absence-minutes` wire format.
  get afterHoursAbsenceMinutesOptions(): Array<{ value: number; label: string }> {
    return [
      { value: 15, label: '15 minutes' },
      { value: 30, label: '30 minutes' },
      { value: 60, label: '1 hour' },
      { value: 120, label: '2 hours' },
      { value: 240, label: '4 hours' },
      { value: 480, label: '8 hours' },
    ];
  }

  get selectedAfterHoursAbsenceMinutes(): { value: number; label: string } | undefined {
    return this.afterHoursAbsenceMinutesOptions.find((option) => option.value === this.afterHoursAbsenceMinutes);
  }

  // ---- Sign-in / Sign-out draft → Save -----------------------------------------------------------

  // The after-hours sign-in value as persisted: forced to "allowed" (true) unless business hours is
  // configured (the toggle is disabled then), so a block that can't take effect is never written —
  // mirrors the after-hours auto sign-out handling in signOutDraft. Shared by the draft and the
  // Multi-signal save overlay so both modes persist the same value.
  get afterHoursSignInForSave(): boolean {
    return this.businessHoursConfigured ? this.enableAfterHoursSignIn : true;
  }

  // The Sign-in `settings` object built from the toggle drafts. The geo-location radius is included
  // only when the feature flag is on (matching the PUT contract / persisted snapshot).
  get signInDraft(): SettingsPayload {
    // The single after-hours toggle fans its value out to every signal type.
    const afterHours = this.afterHoursSignInForSave;
    const geoLocationEntry: SettingsPayload['geo-location'] = {
      enabled: this.geoLocationEnabled,
      schedule: null,
      'enable-after-hours-sign-in': afterHours,
    };
    if (this.isGeofenceRadiusEnabled) {
      geoLocationEntry['geofence-radius-meters'] = this.geoLocationRadius;
    }
    return {
      badge: { enabled: this.badgeEnabled, schedule: null, 'enable-after-hours-sign-in': afterHours },
      wifi: { enabled: this.wifiEnabled, schedule: null, 'enable-after-hours-sign-in': afterHours },
      sso: { enabled: this.ssoEnabled, schedule: null, 'enable-after-hours-sign-in': afterHours },
      'geo-location': geoLocationEntry,
      manual: { enabled: this.manualEnabled, schedule: null, 'enable-after-hours-sign-in': afterHours },
      mdm: { enabled: this.mdmEnabled, schedule: null, 'enable-after-hours-sign-in': afterHours },
    };
  }

  // The Sign-out `signout-settings` object built from the aggregated / per-signal / after-hours drafts.
  get signOutDraft(): SignoutPayload {
    const signalTypeAbsence: SignalTypeAbsencePayload = {};
    if (this.badgeAutoSignOutEnabled) signalTypeAbsence.badge = Math.floor(this.badgeAutoSignOutDuration / 60000);
    if (this.wifiAutoSignOutEnabled) signalTypeAbsence.wifi = Math.floor(this.wifiAutoSignOutDuration / 60000);
    if (this.ssoAutoSignOutEnabled) signalTypeAbsence.sso = Math.floor(this.ssoAutoSignOutDuration / 60000);
    if (this.mdmAutoSignOutEnabled) signalTypeAbsence.mdm = Math.floor(this.mdmAutoSignOutDuration / 60000);
    if (this.geoLocationAutoSignOutEnabled) signalTypeAbsence['geo-location'] = 60; // unused by entry-recorder for active sign-out

    const anyPerSignalTypeEnabled =
      this.badgeAutoSignOutEnabled ||
      this.wifiAutoSignOutEnabled ||
      this.ssoAutoSignOutEnabled ||
      this.mdmAutoSignOutEnabled ||
      this.geoLocationAutoSignOutEnabled;
    const anyAutoSignOutEnabled = this.absenceOfSignalsEnabled || anyPerSignalTypeEnabled;

    // Force the after-hours opt-in off unless business hours is configured (the toggle is disabled
    // then), so we never persist an opt-in that can't take effect.
    const afterHoursEnabled = this.businessHoursConfigured && this.afterHoursAutoSignOutEnabled;

    return {
      enabled: anyAutoSignOutEnabled,
      'signal-absence-minutes': this.absenceOfSignalsEnabled ? Math.floor(this.absenceOfSignalsDuration / 60000) : null,
      'signal-type-absence': Object.keys(signalTypeAbsence).length > 0 ? signalTypeAbsence : null,
      'enable-after-hours-auto-sign-out': afterHoursEnabled,
      'after-hours-absence-minutes': afterHoursEnabled
        ? this.afterHoursAbsenceMinutes
        : AFTER_HOURS_ABSENCE_MINUTES_DEFAULT,
    };
  }

  // Dirty = draft differs from the last saved/loaded snapshot. Both sides come from the same draft
  // getter (the snapshot is captured from it), so the key order is stable for the JSON compare.
  get signInDirty(): boolean {
    return JSON.stringify(this.signInDraft) !== JSON.stringify(this.persistedSignIn);
  }

  // The after-hours toggle is editable in BOTH sign-in modes, but the per-signal `settings` draft only
  // drives the dirty-diff in Allow-any mode (Multi-signal saves send the persisted snapshot). Track
  // after-hours dirtiness explicitly — against the value on the persisted snapshot, which the UI writes
  // uniformly to every signal — so a lone toggle change can be saved in Multi-signal mode too.
  get afterHoursSignInDirty(): boolean {
    const persisted = this.persistedSignIn?.badge?.['enable-after-hours-sign-in'] ?? true;
    return this.afterHoursSignInForSave !== persisted;
  }

  // Overlay the current after-hours value onto a per-signal settings payload, leaving every other field
  // (enabled, geofence radius) untouched. Used for Multi-signal saves, where the per-signal settings
  // come from the persisted snapshot (not the live draft) but the after-hours toggle is still editable.
  settingsWithAfterHours(settings: SettingsPayload): SettingsPayload {
    const v = this.afterHoursSignInForSave;
    return {
      badge: { ...settings.badge, 'enable-after-hours-sign-in': v },
      wifi: { ...settings.wifi, 'enable-after-hours-sign-in': v },
      sso: { ...settings.sso, 'enable-after-hours-sign-in': v },
      'geo-location': { ...settings['geo-location'], 'enable-after-hours-sign-in': v },
      manual: { ...settings.manual, 'enable-after-hours-sign-in': v },
      mdm: { ...settings.mdm, 'enable-after-hours-sign-in': v },
    };
  }

  get signOutDirty(): boolean {
    return JSON.stringify(this.signOutDraft) !== JSON.stringify(this.persistedSignOut);
  }

  // Validation is mode-specific. Allow-any mode validates the geofence radius input — the only
  // editable Sign-in input that can be invalid (and, since the radius is stored on the location's
  // presence settings, it is configured here only, never from Multi-signal mode). Multi-signal mode
  // validates the policy.
  // Each error only blocks Save while its input is rendered: the radius row exists only in Allow-any
  // mode, so a stale radius error can't keep the (radius-less) Multi-signal form unsaveable.
  get signInValid(): boolean {
    if (this.signInMode === 'multi-signal') return this.multiSignalPolicyValid;
    return !(this.isGeofenceRadiusEnabled && this.geoLocationEnabled && Boolean(this.geoLocationRadiusError));
  }

  // Every Sign-out input is a constrained dropdown, so the draft is always valid.
  get signOutValid(): boolean {
    return true;
  }

  get canSaveSignIn(): boolean {
    // Per-signal settings (incl. geofence radius) are editable only in Allow-any mode, so their
    // dirtiness enables Save only there; Multi-signal mode saves the policy alone. (Switching modes
    // flips the policy's `enabled`, so a mode change is always captured by multiSignalPolicyDirty.)
    // The after-hours toggle is editable in both modes, so its dirtiness enables Save in either.
    const settingsDirty = this.signInMode === 'any' && this.signInDirty;
    return (settingsDirty || this.afterHoursSignInDirty || this.multiSignalPolicyDirty) && this.signInValid;
  }

  get signInSaveDisabled(): boolean {
    return !this.canSaveSignIn;
  }

  // ---- Multi-signal sign-in policy, gated into the Sign-in tab's Save above ----------------------

  // Styling for each mode tile — the selected tile gets the brand border at full strength; the other
  // is dimmed (gray border + reduced opacity) so the active choice reads clearly. The title gets a
  // matching emphasis (bold default heading color when selected, muted otherwise).
  get allowAnyTileClass(): string {
    return this.signInMode === 'any' ? 'border-gem-50' : 'border-gray-200 opacity-75';
  }

  get multiSignalTileClass(): string {
    return this.signInMode === 'multi-signal' ? 'border-gem-50' : 'border-gray-200 opacity-75';
  }

  get allowAnyTitleClass(): string {
    return this.signInMode === 'any' ? 'font-bold' : 'font-semibold text-gray-500';
  }

  get multiSignalTitleClass(): string {
    return this.signInMode === 'multi-signal' ? 'font-bold' : 'font-semibold text-gray-500';
  }

  // Selectable corroboration windows (minutes on the wire — distinct from the ms-based sign-out
  // durations). Mirrors the absence-of-signals dropdown shape so the template reuses the same pattern.
  get multiSignalWindowOptions(): Array<{ value: number; label: string }> {
    return [
      { value: 15, label: '15 minutes' },
      { value: 30, label: '30 minutes' },
      { value: 60, label: '1 hour' },
      { value: 120, label: '2 hours' },
      { value: 240, label: '4 hours' },
    ];
  }

  get selectedMultiSignalWindow(): { value: number; label: string } | undefined {
    return this.multiSignalWindowOptions.find((option) => option.value === this.multiSignalWindowMinutes);
  }

  // Per-policy display data for the template: pills for the collapsed view, expanded flag.
  // Pills are ordered by SIGNAL_TYPE_ORDER regardless of the order types were toggled.
  @cached
  get policyDisplayData(): Array<{
    index: number;
    types: string[];
    expanded: boolean;
    pills: Array<{ label: string; fill: FillColorString }>;
  }> {
    return this.signalTypePolicies.map((types, index) => ({
      index,
      types,
      expanded: this.expandedPolicyIndices.has(index),
      pills: SIGNAL_TYPE_ORDER.filter((type) => types.includes(type)).map((type) => SIGNAL_TYPE_PILL_CONFIG[type]!),
    }));
  }

  // The `multi-signal-policy` block built from the draft. `enabled` mirrors the mode selector; the
  // policies and window are always carried (so switching to 'any' preserves them for re-enable).
  get multiSignalPolicyDraft(): MultiSignalPolicyPayload {
    return {
      enabled: this.signInMode === 'multi-signal',
      'signal-type-combinations': this.signalTypePolicies.filter((combo) => combo.length > 0),
      'corroboration-window-minutes': this.multiSignalWindowMinutes,
    };
  }

  // In multi-signal mode the policy must be fully configured before Save: ≥1 policy exists,
  // every policy has ≥1 type, and the window is set. (The BE enforces the same — this gates the
  // button and avoids a round-trip.) In 'any' mode there's nothing to validate.
  get multiSignalPolicyValid(): boolean {
    if (this.signInMode !== 'multi-signal') return true;
    return (
      this.signalTypePolicies.length >= 1 &&
      this.signalTypePolicies.every((combo) => combo.length >= 1) &&
      this.multiSignalWindowMinutes != null
    );
  }

  get multiSignalPolicyDirty(): boolean {
    return JSON.stringify(this.multiSignalPolicyDraft) !== JSON.stringify(this.persistedMultiSignalPolicy);
  }

  get canSaveSignOut(): boolean {
    return this.signOutDirty && this.signOutValid;
  }

  get signOutSaveDisabled(): boolean {
    return !this.canSaveSignOut;
  }

  // Seed the persisted snapshot from the API's business-hours block (read-only — business hours is
  // edited on the per-location Location settings page now). Drives `businessHoursConfigured` (which
  // gates the Sign-in/Sign-out after-hours toggles) and the value carried through each tab's Save.
  #loadBusinessHours(businessHours?: PresenceSettingsResponse['data']['attributes']['business-hours']): void {
    this.persistedBusinessHours = businessHours
      ? {
          enabled: businessHours.enabled,
          'after-hours-report-enabled': businessHours['after-hours-report-enabled'] ?? false,
          days: businessHours.days ?? {},
        }
      : null;
  }

  displayTimeoutPeriod = (timeout: number): string => {
    return MILLISECONDS_TO_TIME_MAP[timeout] || '';
  };

  openInactivityBox = (): void => {
    this.isInactivityBoxOpen = true;
  };

  closeInactivityBox = (): void => {
    this.isInactivityBoxOpen = false;
    this.inactivityTimeout = this.companyConfiguration?.sessionInactivityTimeout || null;
  };

  togglePresenceControlCenter = (): void => {
    this.isPresenceControlCenterOpen = !this.isPresenceControlCenterOpen;
  };

  selectPresenceTab = (tabName: 'sign-in' | 'sign-out'): void => {
    this.selectedPresenceTab = tabName;
  };

  selectLocation = async (option: LocationOption): Promise<void> => {
    this.selectedLocationId = option.location.id;
    await this.loadPresenceSettings(this.selectedLocationId);
    await this.loadLocationAutoSignOut(this.selectedLocationId);
  };

  // Deep-link to the selected location's "Automatic sign-out" settings. We can't rely on a
  // <LinkTo> with a `location` query param — the application route only reads that param on a
  // full page boot, not on in-app transitions — so we switch the current location and
  // transition the same way the locations table's "View settings" link does.
  viewLocationSettings = (): void => {
    if (!this.selectedLocationId) return;
    this.protectedController.send('switchLocation', this.selectedLocationId, 'manage.location-settings');
  };

  // Deep-link like viewLocationSettings, but also scroll to the Business hours panel — used by the
  // Sign-in / Sign-out tabs' "Configure business hours" links. The scrollTo query param is consumed by
  // the location-settings index route (scrolls to the element with id="business-hours").
  configureBusinessHours = (): void => {
    if (!this.selectedLocationId) return;
    this.protectedController.send('switchLocation', this.selectedLocationId, 'manage.location-settings', {
      queryParams: { scrollTo: 'business-hours' },
    });
  };

  // Sign-in / Sign-out controls mutate draft state only; nothing is persisted until that tab's Save.

  // ---- Multi-signal sign-in policy controls (draft only; persisted on the Sign-in tab's Save) -----

  selectSignInMode = (mode: 'any' | 'multi-signal'): void => {
    this.signInMode = mode;
  };

  addPolicy = (): void => {
    const newIndex = this.signalTypePolicies.length;
    this.signalTypePolicies = [...this.signalTypePolicies, []];
    this.expandedPolicyIndices = new Set([...this.expandedPolicyIndices, newIndex]);
  };

  removePolicy = (index: number): void => {
    this.signalTypePolicies = this.signalTypePolicies.filter((_, i) => i !== index);
    // Shift indices > removed down by 1.
    const updated = new Set<number>();
    this.expandedPolicyIndices.forEach((i) => {
      if (i < index) updated.add(i);
      else if (i > index) updated.add(i - 1);
    });
    this.expandedPolicyIndices = updated;
  };

  togglePolicyExpanded = (index: number): void => {
    if (this.expandedPolicyIndices.has(index)) {
      this.expandedPolicyIndices = new Set([...this.expandedPolicyIndices].filter((i) => i !== index));
    } else {
      this.expandedPolicyIndices = new Set([...this.expandedPolicyIndices, index]);
    }
  };

  // Handles the native checkbox `change` event. Adds or removes `type` from the policy at
  // `comboIndex` based on `event.target.checked`. Always replaces the array so Glimmer re-renders.
  togglePolicyType = (comboIndex: number, type: string, event: Event): void => {
    const enabled = (event.target as HTMLInputElement).checked;
    this.signalTypePolicies = this.signalTypePolicies.map((combo, i) => {
      if (i !== comboIndex) return combo;
      if (enabled) {
        return combo.includes(type) ? combo : [...combo, type];
      } else {
        return combo.filter((t) => t !== type);
      }
    });
  };

  selectMultiSignalWindow = (option: { value: number; label: string }): void => {
    this.multiSignalWindowMinutes = option.value;
  };

  selectAbsenceOfSignalsDuration = (option: { value: number; label: string }): void => {
    this.absenceOfSignalsDuration = option.value;
  };

  selectBadgeDuration = (option: { value: number; label: string }): void => {
    this.badgeAutoSignOutDuration = option.value;
  };

  selectWifiDuration = (option: { value: number; label: string }): void => {
    this.wifiAutoSignOutDuration = option.value;
  };

  selectSsoDuration = (option: { value: number; label: string }): void => {
    this.ssoAutoSignOutDuration = option.value;
  };

  selectMdmDuration = (option: { value: number; label: string }): void => {
    this.mdmAutoSignOutDuration = option.value;
  };

  selectAfterHoursAbsenceMinutes = (option: { value: number; label: string }): void => {
    this.afterHoursAbsenceMinutes = option.value;
  };

  validateGeofenceRadius = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    const raw = target.value.trim();
    const errorMsg = `Radius must be a whole number between ${GEOFENCE_RADIUS_MIN} and ${GEOFENCE_RADIUS_MAX} meters`;
    if (raw === '' || !/^-?\d+$/.test(raw)) {
      this.geoLocationRadiusError = errorMsg;
      return;
    }
    const value = Number(raw);
    if (value < GEOFENCE_RADIUS_MIN || value > GEOFENCE_RADIUS_MAX) {
      this.geoLocationRadiusError = errorMsg;
      return;
    }
    this.geoLocationRadius = value;
    this.geoLocationRadiusError = null;
  };

  // Persist just the Sign-in tab: sends the Sign-in draft plus the other tabs' persisted snapshots.
  saveSignIn = async (): Promise<void> => {
    if (!this.canSaveSignIn) return;
    await this.savePresenceSettings('sign-in');
  };

  // Persist just the Sign-out tab: sends the Sign-out draft plus the other tabs' persisted snapshots.
  saveSignOut = async (): Promise<void> => {
    if (!this.canSaveSignOut) return;
    await this.savePresenceSettings('sign-out');
  };

  /**
   * The tasks below are cancelled when the route exits, so the save and the timer reconciliation live
   * in one plain promise: a cancelled continuation would leave the saved setting unenforced.
   */
  async #persistInactivityTimeout(
    companyConfiguration: CompanyConfiguration,
    timeout: number | null,
    reconcileTimer: () => void | Promise<void>,
  ): Promise<void> {
    companyConfiguration.sessionInactivityTimeout = timeout;
    await companyConfiguration.save();
    await reconcileTimer();
  }

  enableInactivityTask = dropTask(async () => {
    const companyConfiguration = this.companyConfiguration;
    try {
      if (companyConfiguration) {
        this.inactivityTimeout = 7200000;
        // start the timer
        await this.#persistInactivityTimeout(companyConfiguration, this.inactivityTimeout, () =>
          this.inactivity.initialize(),
        );
        this.flashMessages.showAndHideFlash('success', 'Inactivity based timeout enabled');
        this.openInactivityBox();
      }
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong, please try again');
    }
  });

  disableInactivityTask = dropTask(async () => {
    const companyConfiguration = this.companyConfiguration;
    try {
      if (companyConfiguration) {
        this.inactivityTimeout = null;
        // stop the timer
        await this.#persistInactivityTimeout(companyConfiguration, this.inactivityTimeout, () =>
          this.inactivity.cleanUp(),
        );
        this.flashMessages.showAndHideFlash('success', 'Inactivity based timeout disabled');
        this.isInactivityBoxOpen = false;
      }
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong, please try again');
    }
  });

  saveInactivityTask = dropTask(async () => {
    const companyConfiguration = this.companyConfiguration;
    const timeout = this.inactivityTimeout;
    try {
      if (companyConfiguration) {
        // change the timeout duration, applied on the next activity-triggered restart
        await this.#persistInactivityTimeout(companyConfiguration, timeout, () => {
          this.inactivity.timeoutDuration = timeout;
        });
        this.flashMessages.showAndHideFlash('success', 'Inactivity timeout period updated');
        this.isInactivityBoxOpen = false;
      }
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong, please try again');
    }
  });

  // Parses the backend "subsumes" error detail string into a human-readable message that names
  // the conflicting policies by their 1-indexed number and their signal types using display names.
  // Returns null if the detail string doesn't match the expected format (caller falls back to the
  // generic message).
  //
  // Expected format:
  //   "combination N ([type1, type2, ...]) subsumes combination M ([typeA, typeB, ...]); combination M would never fire"
  // where N and M are 0-indexed integers. N is the simpler (subset) policy; M is the unreachable one.
  #parseSubsumesError(detail: string): string | null {
    try {
      const match = detail.match(/combination (\d+) \(\[([^\]]*)\]\) subsumes combination (\d+) \(\[([^\]]*)\]\)/);
      if (!match) return null;

      const simplerIndex = parseInt(match[1]!, 10);
      const simplerTypes = (match[2] ?? '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const unreachableIndex = parseInt(match[3]!, 10);
      const unreachableTypes = (match[4] ?? '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const toDisplayName = (type: string): string => SIGNAL_TYPE_PILL_CONFIG[type]?.label ?? type;

      const simplerPolicyNum = simplerIndex + 1;
      const unreachablePolicyNum = unreachableIndex + 1;
      const simplerNames = simplerTypes.map(toDisplayName).join(', ');
      const unreachableNames = unreachableTypes.map(toDisplayName).join(', ');

      return (
        `Policy ${simplerPolicyNum} (${simplerNames}) is simpler than Policy ${unreachablePolicyNum} (${unreachableNames}) and makes it unreachable. ` +
        `Remove the overlap or add a unique signal type to Policy ${unreachablePolicyNum}.`
      );
    } catch {
      return null;
    }
  }

  async loadPresenceSettings(locationId: string): Promise<void> {
    if (!locationId) return;

    try {
      this.isLoading = true;
      this.error = null;

      const url = urlBuilder.presenceSettingsUrl(locationId);

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      const response = (await this.ajax.request(url, {
        method: 'GET',
      })) as PresenceSettingsResponse;

      // Map API response to UI state
      const settings = response.data?.attributes?.settings || {};

      this.badgeEnabled = settings.badge?.enabled ?? true;
      this.wifiEnabled = settings.wifi?.enabled ?? true;
      this.ssoEnabled = settings.sso?.enabled ?? true;
      this.geoLocationEnabled = settings['geo-location']?.enabled ?? true;
      this.manualEnabled = settings.manual?.enabled ?? true;
      this.mdmEnabled = settings.mdm?.enabled ?? true;

      // After-hours sign-in is a single toggle written uniformly to every signal type, but read it
      // defensively: reflect a block (false) if ANY present signal has it disabled, so a location
      // where one signal is absent but another is blocked doesn't silently default to allowed and
      // re-enable after-hours sign-in on the next save. Defaults true (allowed) when no signal
      // specifies it — older rows / locations that never set it keep today's behavior.
      const signalEntries = [
        settings.badge,
        settings.wifi,
        settings.sso,
        settings['geo-location'],
        settings.manual,
        settings.mdm,
      ];
      this.enableAfterHoursSignIn = !signalEntries.some((s) => s?.['enable-after-hours-sign-in'] === false);

      // Geofence radius — only overwrite the tracked default when the API surfaces a real
      // value. `null` (column unset on the server) means "use the display default 150".
      const apiRadius = settings['geo-location']?.['geofence-radius-meters'];
      if (apiRadius != null) {
        this.geoLocationRadius = apiRadius;
      } else {
        this.geoLocationRadius = GEOFENCE_RADIUS_DEFAULT;
      }
      this.geoLocationRadiusError = null;

      // Load sign-out settings
      const signoutSettings = response.data?.attributes?.['signout-settings'];
      const signalTypeAbsence = signoutSettings?.['signal-type-absence'];
      const signalAbsenceMinutes = signoutSettings?.['signal-absence-minutes'];

      // Load per-day business hours (top-level, location-level — not under signout-settings).
      // Seed both the per-day drafts and the persisted snapshot so the section starts clean
      // (not dirty) and auto-saves from other controls preserve the stored config.
      this.#loadBusinessHours(response.data?.attributes?.['business-hours']);

      // Load after-hours auto sign-out (EIP-2017). Opt-in + interval, defaulting off / 60.
      this.afterHoursAutoSignOutEnabled = signoutSettings?.['enable-after-hours-auto-sign-out'] ?? false;
      this.afterHoursAbsenceMinutes =
        signoutSettings?.['after-hours-absence-minutes'] ?? AFTER_HOURS_ABSENCE_MINUTES_DEFAULT;

      // Load aggregated settings (independent of per-signal-type)
      if (signalAbsenceMinutes != null) {
        this.absenceOfSignalsEnabled = signoutSettings?.enabled ?? false;
        this.absenceOfSignalsDuration = signalAbsenceMinutes * 60000;
      } else {
        this.absenceOfSignalsEnabled = false;
        this.absenceOfSignalsDuration = 3600000;
      }

      // Load per-signal-type settings (independent of aggregated)
      if (signalTypeAbsence) {
        this.badgeAutoSignOutEnabled = signalTypeAbsence.badge != null;
        this.badgeAutoSignOutDuration = signalTypeAbsence.badge ? signalTypeAbsence.badge * 60000 : 3600000;

        this.wifiAutoSignOutEnabled = signalTypeAbsence.wifi != null;
        this.wifiAutoSignOutDuration = signalTypeAbsence.wifi ? signalTypeAbsence.wifi * 60000 : 3600000;

        this.ssoAutoSignOutEnabled = signalTypeAbsence.sso != null;
        this.ssoAutoSignOutDuration = signalTypeAbsence.sso ? signalTypeAbsence.sso * 60000 : 3600000;

        this.mdmAutoSignOutEnabled = signalTypeAbsence.mdm != null;
        this.mdmAutoSignOutDuration = signalTypeAbsence.mdm ? signalTypeAbsence.mdm * 60000 : 3600000;

        this.geoLocationAutoSignOutEnabled = signalTypeAbsence['geo-location'] != null;
      } else {
        // Default to OFF - user must opt-in to per-signal-type
        this.badgeAutoSignOutEnabled = false;
        this.badgeAutoSignOutDuration = 3600000;

        this.wifiAutoSignOutEnabled = false;
        this.wifiAutoSignOutDuration = 3600000;

        this.ssoAutoSignOutEnabled = false;
        this.ssoAutoSignOutDuration = 3600000;

        this.mdmAutoSignOutEnabled = false;
        this.mdmAutoSignOutDuration = 3600000;

        this.geoLocationAutoSignOutEnabled = false;
      }

      // Load the multi-signal sign-in policy. Absent (or disabled) ⇒ 'any' mode; policies
      // default to a single empty row and the window unset, matching a location with no policy.
      // Backward compat: if the API returns the old flat `required-signal-types` format, treat it
      // as a single policy so cached responses don't break the UI.
      const multiSignalPolicy = response.data?.attributes?.['multi-signal-policy'];
      this.signInMode = multiSignalPolicy?.enabled ? 'multi-signal' : 'any';
      this.multiSignalWindowMinutes = multiSignalPolicy?.['corroboration-window-minutes'] ?? null;
      if (multiSignalPolicy?.['signal-type-combinations']?.length) {
        this.signalTypePolicies = multiSignalPolicy['signal-type-combinations'];
        this.expandedPolicyIndices = new Set(); // existing configured policies start collapsed
      } else if (multiSignalPolicy?.['required-signal-types']?.length) {
        // Old format: wrap the flat array as a single policy.
        this.signalTypePolicies = [multiSignalPolicy['required-signal-types']];
        this.expandedPolicyIndices = new Set(); // start collapsed
      } else {
        this.signalTypePolicies = [[]];
        this.expandedPolicyIndices = new Set([0]); // empty row starts expanded
      }

      // Capture the loaded state as each tab's persisted snapshot so all three tabs start not-dirty.
      // (#loadBusinessHours already set persistedBusinessHours.) Built from the same draft getters the
      // dirty-diff and Save use, so they compare equal until the admin edits something.
      this.persistedSignIn = this.signInDraft;
      this.persistedSignOut = this.signOutDraft;
      this.persistedMultiSignalPolicy = this.multiSignalPolicyDraft;
    } catch (_error) {
      this.error = 'Failed to load presence settings';
      this.flashMessages.showAndHideFlash('error', 'Failed to load presence settings');
      // Reset to defaults on error
      this.badgeEnabled = false;
      this.wifiEnabled = false;
      this.ssoEnabled = false;
      this.geoLocationEnabled = false;
      this.manualEnabled = false;
      this.mdmEnabled = false;
      this.enableAfterHoursSignIn = true;

      this.badgeAutoSignOutEnabled = false;
      this.badgeAutoSignOutDuration = 3600000;
      this.wifiAutoSignOutEnabled = false;
      this.wifiAutoSignOutDuration = 3600000;
      this.ssoAutoSignOutEnabled = false;
      this.ssoAutoSignOutDuration = 3600000;
      this.mdmAutoSignOutEnabled = false;
      this.mdmAutoSignOutDuration = 3600000;
      this.geoLocationAutoSignOutEnabled = false;

      this.absenceOfSignalsEnabled = false;
      this.absenceOfSignalsDuration = 3600000;

      this.afterHoursAutoSignOutEnabled = false;
      this.afterHoursAbsenceMinutes = AFTER_HOURS_ABSENCE_MINUTES_DEFAULT;

      this.#loadBusinessHours(undefined);

      this.geoLocationRadius = GEOFENCE_RADIUS_DEFAULT;
      this.geoLocationRadiusError = null;

      this.signInMode = 'any';
      this.signalTypePolicies = [[]];
      this.expandedPolicyIndices = new Set([0]);
      this.multiSignalWindowMinutes = null;

      // Keep the snapshots in sync with the reset draft state so the tabs aren't spuriously dirty.
      this.persistedSignIn = this.signInDraft;
      this.persistedSignOut = this.signOutDraft;
      this.persistedMultiSignalPolicy = this.multiSignalPolicyDraft;
    } finally {
      this.isLoading = false;
    }
  }

  async loadLocationAutoSignOut(locationId: string): Promise<void> {
    if (!locationId) {
      this.locationAutoSignOutEnabled = false;
      return;
    }
    try {
      // `reload: true` — the application adapter never background-reloads records
      // (adapters/application.js#shouldBackgroundReloadRecord), so a cached Location could
      // carry a stale `autoSignOutAtMinutesSinceMidnight`. Force a fresh fetch so the
      // tooltip reflects the location's current setting. Matches the convention used by
      // other location loads that need fresh data (e.g. visitors/settings/location/route).
      const location = await this.store.findRecord('location', locationId, { reload: true });
      this.locationAutoSignOutEnabled = location.autoSignOutAtMinutesSinceMidnight !== null;
    } catch (_error) {
      // The tooltip is purely informational — never block the page on this.
      this.locationAutoSignOutEnabled = false;
    }
  }

  // PUTs the full presence-settings document for one tab's Save. The active `section` contributes its
  // draft; the other section is sent from its *persisted* snapshot, so unsaved edits on the other tab
  // are never persisted. Business hours is read-only here (edited on the per-location Location settings
  // page) and is always sent from its persisted snapshot so a Sign-in/Sign-out save never clears it.
  // The endpoint requires `settings` and treats an omitted business-hours as a delete, so we always
  // send all blocks (the `?? draft` fallbacks only matter before the first load). On success we promote
  // the saved section's snapshot so the tab goes clean.
  async savePresenceSettings(section: 'sign-in' | 'sign-out'): Promise<void> {
    if (!this.selectedLocationId) return;

    try {
      this.error = null;

      // Pulled out of the ternaries below so the `??` fallback isn't nested inside `?:` — Prettier
      // 3.1 (pre-commit hook) and 3.3 (CI) format that nesting differently, which fights on every commit.
      const signInFallback = this.persistedSignIn ?? this.signInDraft;
      const signOutFallback = this.persistedSignOut ?? this.signOutDraft;
      const multiSignalFallback = this.persistedMultiSignalPolicy ?? this.multiSignalPolicyDraft;
      // Per-signal settings (incl. geofence radius) are edited only in Allow-any mode. Send the live
      // draft when saving the Sign-in tab in that mode; a Multi-signal Sign-in save leaves the
      // per-signal settings at their persisted snapshot but still overlays the (both-mode-editable)
      // after-hours toggle. Other tabs send the persisted snapshot untouched.
      let settings: SettingsPayload;
      if (section === 'sign-in') {
        settings = this.signInMode === 'any' ? this.signInDraft : this.settingsWithAfterHours(signInFallback);
      } else {
        settings = signInFallback;
      }
      const signoutSettings = section === 'sign-out' ? this.signOutDraft : signOutFallback;
      // Business hours is edited elsewhere now; always carry the persisted snapshot so this Save
      // preserves the stored config rather than clearing it.
      const businessHours = this.persistedBusinessHours;
      // The multi-signal policy lives on the Sign-in tab; other tabs send the persisted snapshot so
      // their Save never alters it.
      const multiSignalPolicy = section === 'sign-in' ? this.multiSignalPolicyDraft : multiSignalFallback;

      // Stamp the location's IANA timezone at save time so the local HH:mm windows are self-contained
      // for downstream consumers (derived from the location, kept out of the draft/dirty-diff). A null
      // business-hours payload clears the stored config (matches the endpoint's PUT-replace semantics).
      const businessHoursPayload = businessHours
        ? { ...businessHours, timezone: this.selectedLocation?.location.timezone }
        : null;

      const attributes: Record<string, unknown> = {
        settings,
        'signout-settings': signoutSettings,
        // Top-level: business hours is independent of sign-out settings.
        'business-hours': businessHoursPayload,
      };
      attributes['multi-signal-policy'] = multiSignalPolicy;

      const url = urlBuilder.presenceSettingsUrl(this.selectedLocationId);
      await this.ajax.request(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          data: {
            type: 'presence-settings',
            attributes,
          },
        },
      });

      // Promote only the saved section's snapshot to its draft. Promote exactly what was sent
      // (`settings`): in Allow-any that's the live draft; in Multi-signal it's the persisted per-signal
      // settings with the after-hours overlay, so the after-hours change sticks (and isn't re-flagged
      // dirty) while the uneditable per-signal settings stay put.
      if (section === 'sign-in') {
        this.persistedSignIn = settings;
        this.persistedMultiSignalPolicy = this.multiSignalPolicyDraft;
      } else {
        this.persistedSignOut = this.signOutDraft;
      }

      this.flashMessages.showAndHideFlash('success', 'Presence settings saved');
    } catch (error) {
      const detail = (error as { payload?: { errors?: { detail?: string }[] } })?.payload?.errors?.[0]?.detail ?? '';
      const message = detail.includes('subsumes')
        ? this.#parseSubsumesError(detail) ??
          'One of your policies overlaps with another — a policy that covers all the same signal types as a simpler policy can never fire. Remove the overlap or add a unique signal type.'
        : 'Failed to save presence settings';
      this.error = message;
      this.flashMessages.showAndHideFlash('error', message);
    }
  }

  changeMacMappingsSearch = (search: string): void => {
    this.macMappingsSearch = search;
  };

  changeMacMappingsSource = (source: string): void => {
    this.macMappingsSource = source;
  };

  <template>
    <UiPage>
      <div class="w-full">
        <UiHeader>{{this.headerTitle}}</UiHeader>
      </div>

      {{#if this.isInactivityTimeoutEnabled}}
        <h3 class="float-left">Security</h3>

        <SettingsPanel
          @isLoading={{this.isConfigLoading}}
          @isOpen={{this.isInactivityBoxOpen}}
          @onCancel={{this.closeInactivityBox}}
          as |panel|
        >
          <panel.header
            @iconUri="/assets/images/inactivity-icon.svg"
            @title="Inactivity based timeout"
            @showCancel={{false}}
            as |header|
          >
            <header.description>
              {{#if this.companyConfiguration.sessionInactivityTimeout}}
                Automatically log users out of the dashboard after
                <span class="font-bold">{{this.inactivityDescription}}</span>
                of inactivity.
              {{else}}
                Automatically log users out of the dashboard after a specified period of inactivity.
              {{/if}}
            </header.description>

            <header.controls>
              {{#if this.isInactivityBoxOpen}}
                <UiButton class="btn btn--small btn--outlineFill" {{on "click" this.closeInactivityBox}}>
                  Close
                </UiButton>
                <UiButton
                  class="btn btn--small btn--brand"
                  disabled={{this.isSaveBtnDisabled}}
                  {{on "click" (perform this.saveInactivityTask)}}
                >
                  Save
                </UiButton>
              {{else if this.companyConfiguration.sessionInactivityTimeout}}
                <UiButton class="btn btn--small btn--brand" {{on "click" this.openInactivityBox}}>
                  Edit
                </UiButton>
              {{else}}
                <UiButton class="btn btn--small btn--brand" {{on "click" (perform this.enableInactivityTask)}}>
                  Enable
                </UiButton>
              {{/if}}
            </header.controls>
          </panel.header>

          <panel.body>
            <div class="tokenized-form mt-8">
              <label id="inactivity-timeout-period-label" for="inactivity-timeout-period" class="requiredField">
                Log out automatically after being inactive for
              </label>
              <PowerSelect
                class="max-w-xs"
                name="inactivity-timeout-period"
                @placeholder="Choose a timeout period"
                @options={{this.inactivityOptions}}
                @selected={{this.inactivityTimeout}}
                @onChange={{set this "inactivityTimeout"}}
                @ariaLabelledBy="inactivity-timeout-period-label"
                required={{true}}
                as |option|
              >
                {{this.displayTimeoutPeriod option}}
              </PowerSelect>

              <UiButton
                class="btn btn--small btn--outlineFill float-right"
                {{on "click" (perform this.disableInactivityTask)}}
              >
                Disable
              </UiButton>
            </div>
          </panel.body>
        </SettingsPanel>
      {{/if}}

      {{#if this.isPresenceControlCenterEnabled}}
        <h3 class="float-left">Presence</h3>

        <SettingsPanel
          class="presence-control-center-panel"
          @isOpen={{this.isPresenceControlCenterOpen}}
          @onCancel={{this.togglePresenceControlCenter}}
          as |panel|
        >
          <panel.header
            @iconUri="/assets/images/features/auto-signout.svg"
            @title="Presence control center"
            @description="Configure sign-in methods for employee presence across your locations."
            @showCancel={{this.isPresenceControlCenterOpen}}
            @onCancel={{this.togglePresenceControlCenter}}
            as |header|
          >
            <header.controls>
              {{#unless this.isPresenceControlCenterOpen}}
                <UiButton class="btn btn--small btn--brand" {{on "click" this.togglePresenceControlCenter}}>
                  Edit
                </UiButton>
              {{/unless}}
            </header.controls>
          </panel.header>

          <panel.body>
            <div class="tokenized-form space-y-6 mt-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <PowerSelect
                  @placeholder="Select a location"
                  @options={{this.availableLocations}}
                  @selected={{this.selectedLocation}}
                  @onChange={{this.selectLocation}}
                  @searchField="location.name"
                  as |option|
                >
                  {{option.location.name}}
                </PowerSelect>
              </div>

              {{#if this.shouldShowToggles}}
                {{#unless this.isLoading}}
                  <nav class="tabs mt-6" data-test-presence-tabs>
                    <button
                      type="button"
                      class="text-sm mr-4 py-2 inline-block {{if (eq this.selectedPresenceTab 'sign-in') 'active'}}"
                      data-test-presence-tab="sign-in"
                      {{on "click" (fn this.selectPresenceTab "sign-in")}}
                    >
                      Sign-In
                    </button>
                    <button
                      type="button"
                      class="text-sm mr-4 py-2 inline-block {{if (eq this.selectedPresenceTab 'sign-out') 'active'}}"
                      data-test-presence-tab="sign-out"
                      {{on "click" (fn this.selectPresenceTab "sign-out")}}
                    >
                      Sign-Out
                    </button>
                  </nav>
                {{/unless}}
              {{/if}}

              {{#if this.shouldShowToggles}}
                {{#if this.isLoading}}
                  {{! LoadingSpinnerPulse renders `.loader`, which is position:absolute and centers on its
                  nearest positioned ancestor. Wrap it in a `relative` box with a real min-height so the
                  spinner centers *inside* this content-area box (with top margin to sit below the location
                  picker) instead of escaping upward and overlapping the dropdown. }}
                  <div class="relative min-h-[160px] mt-6" data-test-presence-settings-loading>
                    <LoadingSpinnerPulse />
                  </div>
                {{else if (eq this.selectedPresenceTab "sign-in")}}
                  <div class="pt-6" data-test-sign-in-content>
                    <div class="space-y-6">
                      {{! The admin chooses how sign-ins are processed — accept any enabled signal
                      (today's behavior) or require multiple signals to corroborate (mutually exclusive). }}
                      <span class="font-bold" data-test-sign-in-logic>Sign-in logic</span>
                      <fieldset class="flex gap-3 border-b pb-4 mt-2 max-w-2xl" data-test-sign-in-mode>
                        <label class="flex-1 cursor-pointer rounded-lg border-2 p-4 {{this.allowAnyTileClass}}">
                          <input
                            type="radio"
                            name="sign-in-mode"
                            class="sr-only"
                            checked={{eq this.signInMode "any"}}
                            {{on "change" (fn this.selectSignInMode "any")}}
                            data-test-sign-in-mode-any
                          />
                          <span class="{{this.allowAnyTitleClass}}">Allow any signal</span>
                          <p class="text-sm text-gray-600 mt-1">
                            Sign-in an employee when any of the specified signal types are detected.
                          </p>
                        </label>
                        <label class="flex-1 cursor-pointer rounded-lg border-2 p-4 {{this.multiSignalTileClass}}">
                          <input
                            type="radio"
                            name="sign-in-mode"
                            class="sr-only"
                            checked={{eq this.signInMode "multi-signal"}}
                            {{on "change" (fn this.selectSignInMode "multi-signal")}}
                            data-test-sign-in-mode-multi
                          />
                          <span class="{{this.multiSignalTitleClass}}">Multi-signal policy</span>
                          <p class="text-sm text-gray-600 mt-1">
                            Only sign-in an employee when all of the specified signal types are present within the
                            configurable time window.
                          </p>
                        </label>
                      </fieldset>

                      {{#if (eq this.signInMode "any")}}
                        <div class="flex items-start justify-between border-b">
                          <div class="flex-1">
                            <span class="font-bold">Access Control</span>
                            <p class="text-sm text-gray-600 mt-1">
                              Enable sign-ins through your Access Control integrations.
                              <a href="https://envoy.help/en/articles/8125322-enabling-employee-auto-check-in">Learn
                                more</a>
                            </p>
                          </div>
                          <SlideToggle @isChecked={{this.badgeEnabled}} @onClick={{set this "badgeEnabled"}} />
                        </div>

                        <div class="flex items-start justify-between border-b">
                          <div class="flex-1">
                            <span class="font-bold">Wi-Fi</span>
                            <p class="text-sm text-gray-600 mt-1">
                              Automatically sign employees in when they connect to office Wi-Fi.
                              <a href="https://envoy.help/en/articles/8125322-enabling-employee-auto-check-in">Learn
                                more</a>
                            </p>
                          </div>
                          <SlideToggle @isChecked={{this.wifiEnabled}} @onClick={{set this "wifiEnabled"}} />
                        </div>

                        <div class="flex items-start justify-between border-b">
                          <div class="flex-1">
                            <span class="font-bold">SSO</span>
                            <p class="text-sm text-gray-600 mt-1">
                              Use Single Sign-On (SSO) systems for employee sign-ins.
                              <a href="https://envoy.help/en/articles/8125322-enabling-employee-auto-check-in">Learn
                                more</a>
                            </p>
                          </div>
                          <SlideToggle @isChecked={{this.ssoEnabled}} @onClick={{set this "ssoEnabled"}} />
                        </div>

                        <div class="flex items-center justify-between border-b">
                          <div class="flex-1">
                            <span class="font-bold">MDM</span>
                            <p class="text-sm text-gray-600 mt-1">
                              Automatically sign employees in via Mobile Device Management (MDM) integrations.
                              <a href="https://envoy.help/en/articles/8125322-enabling-employee-auto-check-in">Learn
                                more</a>
                            </p>
                          </div>
                          <SlideToggle @isChecked={{this.mdmEnabled}} @onClick={{set this "mdmEnabled"}} />
                        </div>

                        <div class="border-b">
                          <div class="flex items-start justify-between">
                            <div class="flex-1">
                              <span class="font-bold">Geo Location</span>
                              <p class="text-sm text-gray-600 mt-1">
                                Sign-in employees via geo-location through the Envoy mobile app.
                                <a
                                  href="https://envoy.help/en/articles/9145470-automatic-check-in-via-location-mobile"
                                >Learn more</a>
                              </p>
                            </div>
                            <SlideToggle
                              @isChecked={{this.geoLocationEnabled}}
                              @onClick={{set this "geoLocationEnabled"}}
                            />
                          </div>

                          {{#if (and this.geoLocationEnabled this.isGeofenceRadiusEnabled)}}
                            <div class="flex items-center justify-between mt-2 ml-4" data-test-geofence-radius-row>
                              <div class="flex-1">
                                <label for="geofence-radius-input" class="label-reset font-bold">Sign-in radius</label>
                                <p class="text-sm text-gray-600 mt-1">
                                  Maximum distance from the location where users will sign in. ({{this.geofenceRadiusMin}}
                                  -
                                  {{this.geofenceRadiusMax}}
                                  meters)
                                </p>
                                {{#if this.geoLocationRadiusError}}
                                  <p class="text-sm text-red-50 mt-1" data-test-geofence-radius-error>
                                    {{this.geoLocationRadiusError}}
                                  </p>
                                {{/if}}
                              </div>
                              <div class="flex items-baseline">
                                <input
                                  id="geofence-radius-input"
                                  type="number"
                                  class="w-24 border rounded px-2 py-1 text-right"
                                  min={{this.geofenceRadiusMin}}
                                  max={{this.geofenceRadiusMax}}
                                  step="1"
                                  value={{this.geoLocationRadius}}
                                  {{on "input" this.validateGeofenceRadius}}
                                  {{on "change" this.validateGeofenceRadius}}
                                  data-test-geofence-radius-input
                                />
                                <span class="text-sm text-gray-600 ml-2">meters</span>
                              </div>
                            </div>
                          {{/if}}
                        </div>

                        <div class="flex items-center justify-between">
                          <div class="flex-1">
                            <span class="font-bold">Manual</span>
                            <p class="text-sm text-gray-600 mt-1">
                              Allow employees to manually sign in through Slack, Teams, Outlook, GCal, the Envoy mobile
                              app, or dashboard.
                            </p>
                          </div>
                          <SlideToggle @isChecked={{this.manualEnabled}} @onClick={{set this "manualEnabled"}} />
                        </div>
                      {{else}}
                        <div class="space-y-6" data-test-multi-signal-policy>
                          {{! Required corroboration window — shared across all policies. A sign-in fires
                          once ANY policy is fully observed within this window. Required to enable the
                          policy. }}
                          <div class="flex items-start justify-between border-b">
                            <div class="flex-1">
                              <span class="font-bold">Sign-in time window</span>
                              <p class="text-sm text-gray-600 mt-1">
                                All required signals in any policy must be detected within this window to sign in an
                                employee.
                              </p>
                            </div>
                            <PowerSelect
                              @options={{this.multiSignalWindowOptions}}
                              @selected={{this.selectedMultiSignalWindow}}
                              @onChange={{this.selectMultiSignalWindow}}
                              @searchEnabled={{false}}
                              @placeholder="Select a window"
                              class="w-40 ml-4"
                              data-test-multi-signal-window
                              as |option|
                            >
                              {{option.label}}
                            </PowerSelect>
                          </div>

                          {{! Signal type policies: any one fully observed triggers a sign-in.
                          Configured policies start collapsed (pills); new/empty policies start expanded. }}
                          <div>
                            <span class="font-bold">Policies</span>
                            <p class="text-sm text-gray-600 mt-1">
                              Configure one or more sets of signal types that must all be detected to sign in an
                              employee.
                            </p>
                          </div>
                          <div class="space-y-4" data-test-signal-policies>
                            {{#each this.policyDisplayData as |policy|}}
                              <UiCard class="space-y-3" data-test-policy-row>
                                <div class="flex items-center justify-between gap-2">
                                  <div class="flex items-center gap-2 min-w-0">
                                    <span class="font-semibold text-sm text-gray-700 shrink-0">Policy
                                      {{add policy.index 1}}:</span>
                                    {{#unless policy.expanded}}
                                      {{#if policy.pills.length}}
                                        <div class="flex flex-wrap gap-3" data-test-policy-pills>
                                          {{#each policy.pills as |pill|}}
                                            <UiPill @fill={{pill.fill}} @size="small">{{pill.label}}</UiPill>
                                          {{/each}}
                                        </div>
                                      {{else}}
                                        <span class="text-sm text-gray-400 italic" data-test-policy-pills>No signal
                                          types selected</span>
                                      {{/if}}
                                    {{/unless}}
                                  </div>
                                  <div class="flex items-center gap-1 shrink-0">
                                    <button
                                      type="button"
                                      class="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                                      {{on "click" (fn this.togglePolicyExpanded policy.index)}}
                                      data-test-edit-policy
                                    >
                                      {{svgJar "pencil-dark-full" height="16" width="16" class="-mb-0.5"}}
                                    </button>
                                    <button
                                      type="button"
                                      class="p-1 rounded text-gray-500 hover:text-red-600 hover:bg-red-50"
                                      {{on "click" (fn this.removePolicy policy.index)}}
                                      data-test-remove-policy
                                    >
                                      {{svgJar "trash-delete-black" height="16" width="16" class="-mb-0.5"}}
                                    </button>
                                  </div>
                                </div>
                                {{#if policy.expanded}}
                                  <div class="grid grid-cols-2 gap-2">
                                    <label class="flex items-center gap-2 cursor-pointer" data-test-combo-badge>
                                      <input
                                        type="checkbox"
                                        checked={{includes "badge" policy.types}}
                                        {{on "change" (fn this.togglePolicyType policy.index "badge")}}
                                      />
                                      <span>Access Control</span>
                                    </label>
                                    <label class="flex items-center gap-2 cursor-pointer" data-test-combo-wifi>
                                      <input
                                        type="checkbox"
                                        checked={{includes "wifi" policy.types}}
                                        {{on "change" (fn this.togglePolicyType policy.index "wifi")}}
                                      />
                                      <span>Wi-Fi</span>
                                    </label>
                                    <label class="flex items-center gap-2 cursor-pointer" data-test-combo-sso>
                                      <input
                                        type="checkbox"
                                        checked={{includes "sso" policy.types}}
                                        {{on "change" (fn this.togglePolicyType policy.index "sso")}}
                                      />
                                      <span>SSO</span>
                                    </label>
                                    <label class="flex items-center gap-2 cursor-pointer" data-test-combo-mdm>
                                      <input
                                        type="checkbox"
                                        checked={{includes "mdm" policy.types}}
                                        {{on "change" (fn this.togglePolicyType policy.index "mdm")}}
                                      />
                                      <span>MDM</span>
                                    </label>
                                    <label class="flex items-center gap-2 cursor-pointer" data-test-combo-geo-location>
                                      <input
                                        type="checkbox"
                                        checked={{includes "geo-location" policy.types}}
                                        {{on "change" (fn this.togglePolicyType policy.index "geo-location")}}
                                      />
                                      <span>Geo Location</span>
                                    </label>
                                  </div>
                                {{/if}}
                              </UiCard>
                            {{/each}}
                            <button
                              type="button"
                              class="btn btn--link !text-gem-50 !p-0 align-baseline !text-sm !font-normal"
                              {{on "click" this.addPolicy}}
                              data-test-add-policy
                            >
                              + Add policy
                            </button>
                          </div>

                        </div>
                      {{/if}}

                      {{! After-hours sign-in validation (EIP-2017), under a Business hours section header —
                      mirrors the Sign-out tab. One toggle, applied to every sign-in signal type, that allows
                      or blocks auto sign-in for signals whose timestamp is outside the location's business
                      hours. Shown in BOTH sign-in modes (the gate applies regardless of Allow-any vs
                      Multi-signal). Defaults on (allowed); requires business hours configured (Location
                      settings tab), so the toggle is disabled until then. }}
                      <h3 class="text-lg font-bold pt-6 pb-4 border-b" data-test-after-hours-sign-in-heading>Business
                        hours</h3>

                      <div class="flex items-start justify-between" data-test-after-hours-sign-in-row>
                        <div class="flex-1">
                          <span class="font-bold">Outside business hours sign in</span>
                          <p class="text-sm text-gray-600 mt-1">
                            Automatically sign employees in when a signal is detected outside the location's business
                            hours.
                            <button
                              type="button"
                              class="btn btn--link !text-gem-50 !p-0 align-baseline !text-sm !font-normal"
                              {{on "click" this.configureBusinessHours}}
                              data-test-after-hours-sign-in-configure-link
                            >
                              Configure business hours
                            </button>
                          </p>
                          {{#unless this.businessHoursConfigured}}
                            <p class="text-sm text-gray-500 mt-1" data-test-after-hours-sign-in-disabled-notice>
                              Configure business hours in this location's Location settings to enable this.
                            </p>
                          {{/unless}}
                        </div>
                        <SlideToggle
                          @isChecked={{this.enableAfterHoursSignIn}}
                          @isDisabled={{not this.businessHoursConfigured}}
                          @onClick={{set this "enableAfterHoursSignIn"}}
                          data-test-after-hours-sign-in-toggle
                        />
                      </div>

                      <div class="flex justify-end pb-1">
                        <UiButton
                          class="btn btn--small btn--brand"
                          disabled={{this.signInSaveDisabled}}
                          {{on "click" this.saveSignIn}}
                          data-test-sign-in-save
                        >
                          Save
                        </UiButton>
                      </div>
                    </div>
                  </div>
                {{else if (eq this.selectedPresenceTab "sign-out")}}
                  <div class="pt-6" data-test-sign-out-content>
                    <div class="space-y-6">
                      {{#if this.showSignOutFallbackNotice}}
                        <MessageBox
                          @type="attention"
                          @header="This location also has Automatic sign-out enabled"
                          data-test-sign-out-fallback-notice
                        >
                          <p>
                            Presence Control Center signs employees out after the configured period of signal absence.
                            This location's Automatic sign-out setting acts as a fallback, signing everyone out at the
                            scheduled time.
                            <button
                              type="button"
                              class="btn btn--link !text-gem-50 !p-0 align-baseline !text-sm"
                              {{on "click" this.viewLocationSettings}}
                              data-test-sign-out-fallback-notice-link
                            >
                              View location settings
                            </button>
                          </p>
                        </MessageBox>
                      {{/if}}

                      <h3 class="text-lg font-bold pb-4 border-b" data-test-sign-out-signals-heading>Sign out signals</h3>

                      {{! Aggregated Auto Sign-Out }}
                      <div class="flex items-start justify-between border-b">
                        <div class="flex-1">
                          <span class="font-bold">All sign-in signals</span>
                          <p class="text-sm text-gray-600 mt-1">
                            Automatically sign-out employees if no sign-in signals are detected for a specific time
                            period.
                            <a
                              href="https://envoy.help/en/articles/12159459-presence-control-center"
                              class="text-blue-600"
                            >Learn more</a>
                          </p>
                          {{#if this.absenceOfSignalsEnabled}}
                            <div class="mt-4">
                              <PowerSelect
                                @options={{this.absenceOfSignalsDurationOptions}}
                                @selected={{this.selectedAbsenceOfSignalsDuration}}
                                @onChange={{this.selectAbsenceOfSignalsDuration}}
                                @searchEnabled={{false}}
                                class="w-40"
                                as |option|
                              >
                                {{option.label}}
                              </PowerSelect>
                            </div>
                          {{/if}}
                        </div>
                        <SlideToggle
                          @isChecked={{this.absenceOfSignalsEnabled}}
                          @onClick={{set this "absenceOfSignalsEnabled"}}
                        />
                      </div>

                      {{! Access Control (Badge) Auto Sign-Out }}
                      <div class="flex items-start justify-between border-b">
                        <div class="flex-1">
                          <span class="font-bold">Access Control</span>
                          <p class="text-sm text-gray-600 mt-1">
                            Automatically sign-out employees if no access control signal is detected for a specific time
                            period.
                          </p>
                          {{#if this.badgeAutoSignOutEnabled}}
                            <div class="mt-4">
                              <PowerSelect
                                @options={{this.absenceOfSignalsDurationOptions}}
                                @selected={{this.selectedBadgeDuration}}
                                @onChange={{this.selectBadgeDuration}}
                                @searchEnabled={{false}}
                                class="w-40"
                                as |option|
                              >
                                {{option.label}}
                              </PowerSelect>
                            </div>
                          {{/if}}
                        </div>
                        <SlideToggle
                          @isChecked={{this.badgeAutoSignOutEnabled}}
                          @onClick={{set this "badgeAutoSignOutEnabled"}}
                        />
                      </div>

                      {{! Wi-Fi Auto Sign-Out }}
                      <div class="flex items-start justify-between border-b">
                        <div class="flex-1">
                          <span class="font-bold">Wi-Fi</span>
                          <p class="text-sm text-gray-600 mt-1">
                            Automatically sign-out employees if no Wi-Fi signal is detected for a specific time period.
                          </p>
                          {{#if this.wifiAutoSignOutEnabled}}
                            <div class="mt-4">
                              <PowerSelect
                                @options={{this.absenceOfSignalsDurationOptions}}
                                @selected={{this.selectedWifiDuration}}
                                @onChange={{this.selectWifiDuration}}
                                @searchEnabled={{false}}
                                class="w-40"
                                as |option|
                              >
                                {{option.label}}
                              </PowerSelect>
                            </div>
                          {{/if}}
                        </div>
                        <SlideToggle
                          @isChecked={{this.wifiAutoSignOutEnabled}}
                          @onClick={{set this "wifiAutoSignOutEnabled"}}
                        />
                      </div>

                      {{! SSO Auto Sign-Out }}
                      <div class="flex items-start justify-between border-b">
                        <div class="flex-1">
                          <span class="font-bold">SSO</span>
                          <p class="text-sm text-gray-600 mt-1">
                            Automatically sign-out employees if no SSO signal is detected for a specific time period.
                          </p>
                          {{#if this.ssoAutoSignOutEnabled}}
                            <div class="mt-4">
                              <PowerSelect
                                @options={{this.absenceOfSignalsDurationOptions}}
                                @selected={{this.selectedSsoDuration}}
                                @onChange={{this.selectSsoDuration}}
                                @searchEnabled={{false}}
                                class="w-40"
                                as |option|
                              >
                                {{option.label}}
                              </PowerSelect>
                            </div>
                          {{/if}}
                        </div>
                        <SlideToggle
                          @isChecked={{this.ssoAutoSignOutEnabled}}
                          @onClick={{set this "ssoAutoSignOutEnabled"}}
                        />
                      </div>

                      {{! MDM Auto Sign-Out }}
                      <div class="flex items-start justify-between border-b">
                        <div class="flex-1">
                          <span class="font-bold">MDM</span>
                          <p class="text-sm text-gray-600 mt-1">
                            Automatically sign-out employees if no MDM signal is detected for a specific time period.
                          </p>
                          {{#if this.mdmAutoSignOutEnabled}}
                            <div class="mt-4">
                              <PowerSelect
                                @options={{this.absenceOfSignalsDurationOptions}}
                                @selected={{this.selectedMdmDuration}}
                                @onChange={{this.selectMdmDuration}}
                                @searchEnabled={{false}}
                                class="w-40"
                                as |option|
                              >
                                {{option.label}}
                              </PowerSelect>
                            </div>
                          {{/if}}
                        </div>
                        <SlideToggle
                          @isChecked={{this.mdmAutoSignOutEnabled}}
                          @onClick={{set this "mdmAutoSignOutEnabled"}}
                        />
                      </div>

                      {{! Geo Location Auto Sign-Out }}
                      <div class="flex items-start justify-between">
                        <div class="flex-1">
                          <span class="font-bold">Geo Location</span>
                          <p class="text-sm text-gray-600 mt-1">
                            Automatically sign out employees when a geo-location signal detects they have left the
                            workplace.
                          </p>
                        </div>
                        <SlideToggle
                          @isChecked={{this.geoLocationAutoSignOutEnabled}}
                          @onClick={{set this "geoLocationAutoSignOutEnabled"}}
                        />
                      </div>

                      <h3 class="text-lg font-bold pt-6 pb-4 border-b" data-test-after-hours-heading>Business hours</h3>

                      {{! After-hours auto sign-out (EIP-2017) is a modifier on the timers above: signals outside }}
                      {{! the location's business hours sign out after a shorter interval. Requires business hours }}
                      {{! configured (Location settings tab), so the toggle is disabled until then; it also only }}
                      {{! takes effect once a sign-out signal above is enabled. }}
                      <div class="flex items-start justify-between" data-test-after-hours-row>
                        <div class="flex-1">
                          <span class="font-bold">Outside business hours sign out</span>
                          <p class="text-sm text-gray-600 mt-1">
                            Sign employees out sooner when a signal is detected outside the location's business hours.
                            <button
                              type="button"
                              class="btn btn--link !text-gem-50 !p-0 align-baseline !text-sm !font-normal"
                              {{on "click" this.configureBusinessHours}}
                              data-test-configure-business-hours-link
                            >
                              Configure business hours
                            </button>
                          </p>
                          {{#unless this.businessHoursConfigured}}
                            <p class="text-sm text-gray-500 mt-1" data-test-after-hours-disabled-notice>
                              Configure business hours in this location's Location settings to enable this.
                            </p>
                          {{/unless}}
                          {{#if (and this.businessHoursConfigured this.afterHoursAutoSignOutEnabled)}}
                            <div class="mt-4">
                              <PowerSelect
                                @options={{this.afterHoursAbsenceMinutesOptions}}
                                @selected={{this.selectedAfterHoursAbsenceMinutes}}
                                @onChange={{this.selectAfterHoursAbsenceMinutes}}
                                @searchEnabled={{false}}
                                class="w-40"
                                data-test-after-hours-minutes-input
                                as |option|
                              >
                                {{option.label}}
                              </PowerSelect>
                            </div>
                          {{/if}}
                        </div>
                        <SlideToggle
                          @isChecked={{and this.businessHoursConfigured this.afterHoursAutoSignOutEnabled}}
                          @isDisabled={{not this.businessHoursConfigured}}
                          @onClick={{set this "afterHoursAutoSignOutEnabled"}}
                          data-test-after-hours-toggle
                        />
                      </div>

                      <div class="flex justify-end pb-1">
                        <UiButton
                          class="btn btn--small btn--brand"
                          disabled={{this.signOutSaveDisabled}}
                          {{on "click" this.saveSignOut}}
                          data-test-sign-out-save
                        >
                          Save
                        </UiButton>
                      </div>
                    </div>
                  </div>
                {{/if}}
              {{else}}
                <div class="border-t pt-6">
                  <div class="text-center py-8 text-gray-500">
                    <p>Please select a location to configure presence settings.</p>
                  </div>
                </div>
              {{/if}}
            </div>
          </panel.body>
        </SettingsPanel>

        {{#if this.showMacAddressMappings}}
          <MacAddressMappingsTable
            @companyId={{this.companyId}}
            @search={{this.macMappingsSearch}}
            @source={{this.macMappingsSource}}
            @changeSearch={{this.changeMacMappingsSearch}}
            @changeSource={{this.changeMacMappingsSource}}
          />
        {{/if}}
      {{/if}}
    </UiPage>
  </template>
}

export default RouteTemplate(LocationOverviewManageAccountSettingsTemplate);
